/*
 * @Author: zhouzhuoxin
 * @Email: shzhouzhuoxin@jd.com
 * @Date: 2021-03-22 14:20:33
 * @Last Modified by: caiyifan5
 * @Last Modified time: 2022-03-02 14:46:20
 * @Description: Description
 */
import React, { useState, useEffect, useRef, useMemo, useImperativeHandle } from 'react';
import LOAD_STATUS from '@/constants/load-status';
import PopupFollow from '@/components/popup-follow';
import MultipleCaseder from '@zero/multiple-caseader/dist/multiple-cascader.esm';
import '@zero/multiple-caseader/dist/index.less';
import './index.less';
import apis from '@/apis/index';
import { Form, Radio } from 'antd';

const defaultRadioLabels = { all: '全部类目', part: '部分类目' };

function getMergedValue(options: any, values: any) {
  if (!options) return [];
  if (!values) return [];

  const cachedValueDict = values.reduce((res: any, cur: any) => {
    res[cur] = true;
    return res;
  }, {});
  let lengthOfValues = values.length;

  return innerGetMergedValue(options);

  function innerGetMergedValue(innerOptions: any) {
    const selectedValue: any = [];
    if (!innerOptions) return selectedValue;
    if (lengthOfValues === 0) return selectedValue;

    var itemValue = null;
    for (var i = 0, len = innerOptions.length; i < len; i++) {
      itemValue = innerOptions[i].value;

      if (cachedValueDict[itemValue]) {
        lengthOfValues--;
        selectedValue.push(itemValue);
        if (lengthOfValues === 0) break;
      } else {
        if (innerOptions[i].children && innerOptions[i].children.length) {
          const childrenSelectedValue: any = innerGetMergedValue(innerOptions[i].children);
          if (childrenSelectedValue.length === innerOptions[i].children.length) selectedValue.push(itemValue);
          else selectedValue.push(...childrenSelectedValue);
        }
      }
    }

    return selectedValue;
  }
}

// type: 1 类目 2 部门
async function fetchTree(type = 1, level: number) {
  return await (apis as any).statisticTree({ type }, level, true);
}

function TreeSelectDepartment(props: any, ref: any) {
  const {
    level = 2,
    innerAPIType = 1,
    hasSelectAll = false,
    customRequest = () => fetchTree(innerAPIType, level),
    outContainer = [],
    ..._props
  } = props;

  const [data, setData] = useState({
    loadStatus: LOAD_STATUS.ING,
    departments: [],
  });

  useImperativeHandle(ref, () => {
    return {
      mergeValue(value: any) {
        return getMergedValue(data.departments, value);
      },
    };
  });

  useEffect(() => {
    if (outContainer.length > 0) return;
    customRequest().then((res: any = {}) => {
      const { ret } = res;
      if (ret !== '0' || !res.data) return;

      setData({ loadStatus: LOAD_STATUS.SUCCESS, departments: res.data });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (outContainer.length <= 0) return;
    let dataCon = outContainer.slice();
    customRequest().then((res: any = {}) => {
      const { ret } = res;
      if (ret !== '0' || !res.data) return;

      dataCon.forEach((item: any) => {
        if (item.name === '系统类目') {
          item.children = res.data;
        }
      });

      setData({ loadStatus: LOAD_STATUS.SUCCESS, departments: dataCon });
    });

    // eslint-disable-next-line
  }, [outContainer]);

  // 存在选择全部
  if (hasSelectAll) {
    return <TreeSelectAllDepartment hasSelectAll={hasSelectAll} options={data.departments} {..._props} />;
  } else {
    return <TreeSelectDepartmentNormal options={data.departments} {..._props} />;
  }
}

function TreeSelectDepartmentNormal(props: any) {
  const { placeholder = '', onChange, value, options, ..._props } = props;

  return (
    <PopupFollow containerClassName="tree-select-category-wrap-fa">
      <MultipleCaseder
        fieldNames={{ label: 'name', value: 'value' }}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChange}
        {..._props}
      />
    </PopupFollow>
  );
}

const ALL_CATEGORY = 1;
const PART_OF_CATEGORY = 2;
function TreeSelectAllDepartment(props: any) {
  const { placeholder = '', onChange, value, options, hasSelectAll, ..._props } = props;

  const refOnchange = useRef(onChange);
  refOnchange.current = onChange;

  // 所有叶节点值
  const allValue = useMemo(() => {
    if (!options.length) return [];
    return findAllValue(options);
  }, [options]);

  // 所哟第一层节点值
  const cacheFirstDictValue = useMemo(() => {
    return options.reduce((res: any, cur: any) => {
      res[cur.value] = true;
      return res;
    }, {});
  }, [options]);

  function computedCategoryType() {
    // options没有加载完成则计算不出当前分类
    if (!value || !options || !options.length) return null;
    // 如果value长度等于所有叶节点长度选中所有
    if (value.length === allValue.length) return ALL_CATEGORY;
    // 向上合并选择所有，例如选中某个二级的所有三级会被合并为选中的当前二级
    if (value.length === options.length) {
      if (value.every((item: any) => cacheFirstDictValue[item])) return ALL_CATEGORY;
    }
    return PART_OF_CATEGORY;
  }

  const selectedType = computedCategoryType();

  // 保存上一次设置的值
  const preValueRef = useRef(null);

  useEffect(
    function () {
      return () => {
        if (selectedType === PART_OF_CATEGORY) preValueRef.current = value || [];
      };
    },
    [value, selectedType]
  );

  function handleCategoryTypeChange(radioEvent: any) {
    const radioValue = radioEvent.target.value;

    if (!radioValue) return;

    if (radioValue === ALL_CATEGORY) onChange(allValue);
    else onChange(preValueRef.current || []);
  }

  let radioLabels =
    typeof hasSelectAll === 'boolean' ? defaultRadioLabels : hasSelectAll?.radioLabels || defaultRadioLabels;

  return (
    <div className="tree-select-category-wrap" style={props.layout === 'inline' ? { display: 'flex' } : {}}>
      <Form.Item>
        <Radio.Group value={selectedType} onChange={handleCategoryTypeChange}>
          <Radio value={ALL_CATEGORY}>{radioLabels.all}</Radio>
          <Radio value={PART_OF_CATEGORY}>{radioLabels.part}</Radio>
        </Radio.Group>
      </Form.Item>
      {selectedType !== PART_OF_CATEGORY ? null : (
        <Form.Item>
          <PopupFollow>
            <MultipleCaseder
              fieldNames={{ label: 'name', value: 'value' }}
              containerClassName="tree-select-category-wrap-fa"
              placeholder={placeholder}
              options={options}
              value={value}
              onChange={onChange}
              {..._props}
            />
          </PopupFollow>
        </Form.Item>
      )}
    </div>
  );
}

export async function validateSelectMax(rule: any, value: Array<any>) {
  const maxValue = 500;
  if (!value) return Promise.resolve();
  if (value.length > maxValue) throw Error(`最多选择${maxValue}个品类`);
  return Promise.resolve();
}

/**
 *
 * @param tree [{value: 111, name: 'aa', children?: []}]
 */
function findAllValue(tree: any) {
  let values: any[] = [];

  if (!tree || tree.length === 0) return [];

  for (var i = 0, len = tree.length; i < len; i++) {
    if (!tree[i].children || tree[i].children.length === 0) {
      values.push(tree[i].value);
    } else {
      values = values.concat(findAllValue(tree[i].children));
    }
  }

  return values;
}

export default React.forwardRef(TreeSelectDepartment);
